import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Container from '@components/layout/container/container';
import EventTeaserListItem from '@components/molecules/event-teaser-list-item/eventTeaserListItem';
import Headline from '@components/atoms/headline/headline';
import Link from 'next/link';
import ShapedImage from '@components/molecules/shaped-image/shapedImage';
import { useTranslations } from 'next-intl';
import { buildSrcSet } from '@helper';
import StyledEventTeaserList from './eventTeaserList.style';

const EventTeaserList = ({ data, events, accessibilityDefaultTheme }) => {
  const t = useTranslations();
  const [previewImage, setPreviewImage] = useState(events[0]?.image ?? '');

  return (
    <StyledEventTeaserList
      id={data.id}
      colorScheme={data.eventTeaserColorTheme}
      accessibilityDefaultTheme={accessibilityDefaultTheme}
      aria-label={data.eventTeaserHeadline || 'Event Teaser List'}
    >
      <Container large>
        <div className="teaser-list">
          {data.eventTeaserHeadline && (
            <div className="teaser-list__headline">
              <Headline secondary>{data.eventTeaserHeadline}</Headline>
            </div>
          )}
          {data.eventTeaserCta?.[0]?.url && events?.length > 0 && (
            <div className="teaser-list__more">
              <Link href={data.eventTeaserCta[0].url} className="teaser-list__more-cta">
                <Headline subline tag="span">
                  {data.eventTeaserCta[0].title}
                </Headline>
              </Link>
            </div>
          )}
          <div className="teaser-list__preview">
            {previewImage && (
              <div className="teaser-list__preview-sticky-image">
                <ShapedImage
                  colorScheme={data.eventTeaserColorTheme}
                  shadow="left"
                  shadowSize="xl"
                  imageClassName="teaser-list__preview-image"
                  onlyBackground
                  clipForm="polygon(5% 0, 89% 0, 100% 90%, 95% 100%, 0 95%)"
                  src={previewImage.src}
                  srcset={buildSrcSet(previewImage.srcset)}
                  sizes="(min-width: 1280px) 33vw,(min-width: 768px) 25vw, 100vw"
                />
              </div>
            )}
          </div>
          {(events ?? []).length > 0 ? (
            <div className="teaser-list__grid">
              <div className="teaser-list__grid-container">
                {events.map(item => (
                  <div key={item.id} onMouseEnter={() => setPreviewImage(item.image ?? '')}>
                    <EventTeaserListItem
                      hours={item.hours}
                      title={item.headline}
                      uri={item.uri}
                      image={item.image}
                      startTime={item.startTime}
                      endDate={item.endTime}
                      copy={item.description}
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="teaser-list__no-events">
              <p>{t('filter.noResults')}</p>
              {data.eventTeaserCta?.[0]?.url && (
                <Link href={data.eventTeaserCta[0].url} className="teaser-list__more-cta">
                  <Headline subline tag="span">
                    {data.eventTeaserCta[0].title}
                  </Headline>
                </Link>
              )}
            </div>
          )}
        </div>
      </Container>
    </StyledEventTeaserList>
  );
};

EventTeaserList.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.number.isRequired,
    eventTeaserColorTheme: PropTypes.string,
    eventTeaserHeadline: PropTypes.string,
    eventTeaserCta: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired
      })
    ).isRequired
  }).isRequired,
  events: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      hours: PropTypes.string,
      headline: PropTypes.string.isRequired,
      uri: PropTypes.string.isRequired,
      image: PropTypes.shape({
        src: PropTypes.string.isRequired,
        alt: PropTypes.string
      }).isRequired,
      startTime: PropTypes.string.isRequired,
      endTime: PropTypes.string,
      description: PropTypes.string
    })
  ).isRequired,
  accessibilityDefaultTheme: PropTypes.string
};

const mapStateToProps = state => ({
  accessibilityDefaultTheme: state.accessibility.defaultTheme
});

export default connect(mapStateToProps)(EventTeaserList);
